import { type InputRef, Table } from 'antd';
import { type ColumnFilterItem, type FilterDropdownProps } from 'antd/es/table/interface';
import { pick } from 'lodash/fp';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { type MobilityListDataFragment } from '../../../api/fragments/MobilityListData';
import { useListMobilitiesQuery } from '../../../api/queries/listMobilities';
import { CompanyFilterListCollection } from '../../../api/types';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import FilterBox from '../../../components/FilterBox';
import PaginatedTableWithContext from '../../../components/PaginatedTableWithContext';
import InternalErrorResult from '../../../components/results/InternalErrorResult';
import SearchBox from '../../../components/SearchBox';
import renderFilterIcon from '../../../utilities/renderFilterIcon';
import renderSearchIcon from '../../../utilities/renderSearchIcon';
import useGetCompanyFilter from '../../../utilities/useGetCompanyFilter';
import useSortAndFilterCache, { SortAndFilterCacheKey } from '../../../utilities/useSortAndFilterCache';
import useListReducer from './useListReducer';
import useSortOrder, { getSortField } from './useSortOrder';

const MobilityList = () => {
    const { t } = useTranslation('mobilityList');

    const inputRef = useRef<InputRef>(null);

    // get company context
    const company = useCompany(true);

    const [currentCache, setCache] = useSortAndFilterCache(SortAndFilterCacheKey.Mobility);

    // get state from a reducer
    const [state, dispatch] = useListReducer(currentCache);

    // fetch data
    const { page, pageSize, sort, filter } = state;

    const { data, loading, error } = useListMobilitiesQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            pagination: { offset: (page - 1) * pageSize, limit: pageSize },
            sort: pick(['field', 'order'], sort),
            filter: { ...filter, companyIds: company ? [company.id] : filter.companyIds },
        },
    });

    const companyFilterList = useGetCompanyFilter({ collection: CompanyFilterListCollection.Mobilities });

    const onChange = useCallback(
        (pagination, filters, sorter, extra) => {
            switch (extra.action) {
                case 'sort':
                    return dispatch({
                        type: 'setSort',
                        sortBy: getSortField(sorter),
                    });

                case 'filter':
                    return dispatch({
                        type: 'setFilter',
                        filterBy: {
                            companyIds: !filters.company ? undefined : (filters.company as string[]),
                            kind: !filters.kind ? null : filters.kind,
                            referenceName: !filters.referenceName ? null : filters.referenceName[0],
                            title: !filters.title ? null : filters.title[0],
                        },
                    });

                default:
                    return undefined;
            }
        },
        [dispatch]
    );

    const navigate = useNavigate();

    const onRow = useCallback(
        (record: MobilityListDataFragment) => ({
            onClick: () => {
                setCache(state);
                navigate(record.id);
            },
        }),
        [navigate, setCache, state]
    );

    // prepare items as a data source
    const dataSource = useMemo(() => (data?.list?.items || []).map(item => ({ ...item, key: item.id })), [data]);

    const total = data?.list?.count || 0;

    const showFilterDropDown = useCallback(
        (props: FilterDropdownProps, filters: ColumnFilterItem[]) =>
            FilterBox({
                filterDropDownProps: props,
                filters,
            }),
        []
    );

    const showSearchDropDown = useCallback(
        (props, dataIndex) =>
            SearchBox({
                filterDropDownProps: props,
                dataIndex,
                onRef: node => {
                    inputRef.current = node;
                },
            }),
        [inputRef]
    );

    const onFilterDropdownVisibleChange = useCallback(
        visible => {
            if (visible) {
                setTimeout(() => inputRef.current.select(), 100);
            }
        },
        [inputRef]
    );

    const { companySort, referenceNameSort, kindSort } = useSortOrder(sort);

    if (!loading) {
        if (error) {
            return <InternalErrorResult />;
        }
    }

    return (
        <PaginatedTableWithContext
            company={company}
            dataSource={dataSource}
            dispatch={dispatch}
            loading={loading}
            onChange={onChange}
            onRow={onRow}
            rowKey="id"
            state={state}
            tableName={t('mobilityList:options')}
            total={total}
        >
            {!company && (
                <Table.Column
                    key="company"
                    dataIndex={['module', 'company', 'displayName']}
                    filterDropdown={props => showFilterDropDown(props, companyFilterList)}
                    filterIcon={renderFilterIcon}
                    filteredValue={filter.companyIds}
                    sortOrder={companySort}
                    title={t('mobilityList:columns.company')}
                    sorter
                />
            )}
            <Table.Column dataIndex="kind" sortOrder={kindSort} title={t('mobilityList:columns.type')} sorter />
            <Table.Column
                dataIndex="referenceName"
                filterDropdown={props => showSearchDropDown(props, t('mobilityList:columns.referenceName'))}
                filterIcon={renderSearchIcon}
                filteredValue={filter.referenceName ? [filter.referenceName] : undefined}
                onFilterDropdownVisibleChange={onFilterDropdownVisibleChange}
                sortOrder={referenceNameSort}
                title={t('mobilityList:columns.referenceName')}
                sorter
            />
            <Table.Column dataIndex={['title', 'defaultValue']} title={t('mobilityList:columns.title')} />
            <Table.Column dataIndex={['stockIds', 'length']} title={t('mobilityList:columns.vehicles')} />
        </PaginatedTableWithContext>
    );
};

export default MobilityList;
